import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { billingAPI, chatAPI, exploreAPI } from '../services/api';
import { purchaseService } from '../services/purchaseService';
import { useAuth } from '../contexts/AuthContext';
import {
  buildOnboardingProfileUpdate,
  getOnboardingTags,
} from '../utils/onboarding';
import { consumePostAuthRedirect } from '../utils/authRedirect';
import tagsData from '../data/tags.json';
import { getImageUrl } from '../config/runtime';

const TOTAL_STEPS = 4;
const MAX_PREVIEW_CARDS = 4;
const ONBOARDING_DRAFT_STORAGE_KEY = 'okio_onboarding_draft';

const OnboardingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, updateProfile } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedTags, setSelectedTags] = useState(() => {
    try {
      const storedDraft = sessionStorage.getItem(ONBOARDING_DRAFT_STORAGE_KEY);
      if (storedDraft) {
        const parsed = JSON.parse(storedDraft);
        if (Array.isArray(parsed?.selectedTags)) {
          return parsed.selectedTags;
        }
      }
    } catch (_) {}
    return getOnboardingTags(user);
  });
  const [selectedStarterId, setSelectedStarterId] = useState(() => {
    try {
      const storedDraft = sessionStorage.getItem(ONBOARDING_DRAFT_STORAGE_KEY);
      if (storedDraft) {
        const parsed = JSON.parse(storedDraft);
        return parsed?.selectedStarterId || null;
      }
    } catch (_) {}
    return null;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [isRedeemingVoucher, setIsRedeemingVoucher] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    try {
      const storedDraft = sessionStorage.getItem(ONBOARDING_DRAFT_STORAGE_KEY);
      if (storedDraft) {
        const parsed = JSON.parse(storedDraft);
        if (Array.isArray(parsed?.selectedTags)) {
          setSelectedTags(parsed.selectedTags);
        }
        if (parsed?.selectedStarterId) {
          setSelectedStarterId(parsed.selectedStarterId);
        }
        return;
      }
    } catch (_) {}

    setSelectedTags(getOnboardingTags(user));
  }, [user]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        ONBOARDING_DRAFT_STORAGE_KEY,
        JSON.stringify({
          selectedTags,
          selectedStarterId,
        })
      );
    } catch (_) {}
  }, [selectedStarterId, selectedTags]);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
    window.scrollTo(0, 0);
  }, [stepIndex]);

  const selectedTagLabels = useMemo(() => {
    return selectedTags
      .map((tagId) => tagsData.tags.find((tag) => tag.id === tagId)?.label)
      .filter(Boolean);
  }, [selectedTags]);

  const previewQueryKey = useMemo(
    () => ['onboarding-starters-preview', selectedTags.join(',')],
    [selectedTags]
  );

  const fetchPreviewProjects = useCallback(async () => {
    const params = {
      limit: 6,
      sort: 'popularity',
    };

    if (selectedTags.length > 0) {
      params.tags = selectedTags.join(',');
    }

    const response = await exploreAPI.getAll(params);
    return response?.data?.data?.projects || [];
  }, [selectedTags]);

  useEffect(() => {
    if (stepIndex < 1) return;

    queryClient.prefetchQuery({
      queryKey: previewQueryKey,
      queryFn: fetchPreviewProjects,
      staleTime: 60_000,
    });
  }, [fetchPreviewProjects, previewQueryKey, queryClient, stepIndex]);

  const previewQuery = useQuery({
    queryKey: previewQueryKey,
    queryFn: fetchPreviewProjects,
    enabled: stepIndex >= 2,
    staleTime: 60_000,
  });

  const previewProjects = (previewQuery.data || []).slice(0, MAX_PREVIEW_CARDS);
  const selectedStarter = useMemo(
    () =>
      previewQuery.data?.find((project) => project.id === selectedStarterId) ||
      null,
    [previewQuery.data, selectedStarterId]
  );
  const isPreviewLoading =
    stepIndex === 2 &&
    (previewQuery.isPending || previewQuery.isLoading || previewQuery.isFetching) &&
    previewProjects.length === 0;
  const starterDestination = useMemo(() => {
    if (selectedStarterId) {
      return `/starters/${selectedStarterId}`;
    }
    const params = new URLSearchParams();
    if (selectedTags.length > 0) {
      params.set('tags', selectedTags.join(','));
    }
    return `/starters${params.toString() ? `?${params.toString()}` : ''}`;
  }, [selectedStarterId, selectedTags]);

  const finishOnboarding = useCallback(async ({ launchSelectedStarter = false } = {}) => {
    if (!user) return;

    setIsSaving(true);
    try {
      const payload = buildOnboardingProfileUpdate({
        existingSettings: user.app_settings || {},
        selectedTags,
        existingInterests: user.interests || '',
        interestSummary:
          selectedTagLabels.length > 0
            ? `Interested in ${selectedTagLabels.join(', ')}`
            : 'Exploring with Okio',
      });

      await updateProfile(payload);
      try {
        sessionStorage.removeItem(ONBOARDING_DRAFT_STORAGE_KEY);
      } catch (_) {}

      if (launchSelectedStarter && selectedStarterId) {
        const cloneResponse = await exploreAPI.clone(selectedStarterId);
        const projectId = cloneResponse?.data?.data?.projectId;

        if (!projectId) {
          throw new Error('Starter selected but project creation failed');
        }

        const sessionResponse = await chatAPI.startNewSession(projectId);
        const chatId =
          sessionResponse?.data?.data?.chatId || sessionResponse?.data?.chatId;

        if (!chatId) {
          throw new Error('Project created but chat session missing ID');
        }

        navigate(`/projects/${projectId}/chats/${chatId}`, { replace: true });
        return;
      }

      navigate(consumePostAuthRedirect(starterDestination), { replace: true });
    } catch (error) {
      console.error('Failed to save onboarding preferences:', error);
      toast.error('Could not save your onboarding preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [
    navigate,
    selectedTags,
    selectedTagLabels,
    selectedStarterId,
    starterDestination,
    updateProfile,
    user,
  ]);

  const handleContinue = async () => {
    if (stepIndex < TOTAL_STEPS - 1) {
      setStepIndex((current) => current + 1);
      return;
    }

    await finishOnboarding();
  };

  const handleBack = () => {
    if (stepIndex === 0) return;
    setStepIndex((current) => current - 1);
  };

  const toggleTag = (tagId) => {
    setSelectedTags((current) =>
      current.includes(tagId)
        ? current.filter((value) => value !== tagId)
        : [...current, tagId]
    );
  };

  const handleSelectStarter = (starterId) => {
    setSelectedStarterId(starterId);
    setStepIndex(3);
  };

  const handleInlineUpgrade = async () => {
    if (!user || isPurchasing) return;

    setIsPurchasing(true);
    try {
      const result = await purchaseService.purchaseTokens({
        packId: 'topup_100',
        userId: user.id,
        successUrl: `${window.location.origin}/onboarding?checkout=success`,
        cancelUrl: `${window.location.origin}/onboarding?checkout=cancel`,
      });

      if (result?.channel === 'revenuecat') {
        toast.success('Purchase completed. Your token balance is refreshing.');
        queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
        await finishOnboarding({ launchSelectedStarter: true });
      }
    } catch (error) {
      if (error?.userCancelled) {
        return;
      }
      console.error('Onboarding purchase failed:', error);
      toast.error(error?.message || 'Could not start checkout. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRedeemVoucher = async (event) => {
    event.preventDefault();
    if (!user || isRedeemingVoucher || isSaving) return;

    const code = voucherCode.trim();
    if (!code) {
      toast.error('Enter a voucher code first.');
      return;
    }

    setIsRedeemingVoucher(true);
    try {
      const response = await billingAPI.redeemVoucher(code);
      const tokensGranted = response?.data?.data?.tokensGranted;
      queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
      toast.success(
        typeof tokensGranted === 'number'
          ? `Voucher redeemed for ${tokensGranted} Okio tokens.`
          : 'Voucher redeemed.'
      );
      setVoucherCode('');
      await finishOnboarding({ launchSelectedStarter: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Could not redeem that voucher. Please check the code and try again.'
      );
    } finally {
      setIsRedeemingVoucher(false);
    }
  };

  useEffect(() => {
    const checkoutState = new URLSearchParams(location.search).get('checkout');
    if (!checkoutState || !user) return;

    if (checkoutState === 'cancel') {
      toast('Checkout canceled. You can still continue without upgrading.', {
        icon: 'ℹ️',
      });
      navigate('/onboarding', { replace: true });
      return;
    }

    if (checkoutState === 'success') {
      queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
      toast.success('Purchase completed. Finishing your setup...');
      navigate('/onboarding', { replace: true });
      finishOnboarding({ launchSelectedStarter: true });
    }
  }, [finishOnboarding, location.search, navigate, queryClient, user]);

  return (
    <div className="min-h-screen bg-[#f6f3ec] dark:bg-[#0f1311]">
      <style>
        {`
          @keyframes onboardingStepEnter {
            from {
              opacity: 0;
              transform: translateY(14px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-5 sm:px-6 lg:px-8">
        <div ref={topRef} />
        <div className="mb-6 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleBack}
            className={`rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-gray-700 transition dark:border-white/10 dark:text-gray-200 ${
              stepIndex === 0 ? 'pointer-events-none opacity-0' : 'hover:bg-black/5 dark:hover:bg-white/5'
            }`}
          >
            Back
          </button>
          <img
            src="/logo_black.webp"
            alt="OKIO"
            className="h-7 w-auto dark:hidden"
          />
          <img
            src="/logo_white.webp"
            alt="OKIO"
            className="hidden h-7 w-auto dark:block"
          />
          <div className="w-[72px] text-right text-sm font-medium text-gray-500 dark:text-gray-400">
            {stepIndex + 1}/{TOTAL_STEPS}
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-between gap-6 rounded-[2rem] bg-white px-5 py-6 shadow-[0_10px_40px_rgba(0,0,0,0.08)] dark:bg-[#161b18] sm:px-8 sm:py-8">
          <div
            key={stepIndex}
            className="space-y-6"
            style={{
              animation: 'onboardingStepEnter 280ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {stepIndex === 0 && (
              <section className="space-y-6">
                <div className="max-w-4xl space-y-3">
                  <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
                    Okio helps you explore the real world with an expert guide in your pocket.
                  </h1>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300 sm:text-lg">
                    We will quickly tune your starting experience so your first projects feel relevant from the first tap.
                  </p>
                </div>
                <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-[#f3efe5] shadow-[0_14px_40px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-[#111714]">
                  <img
                    src="/home/forrest2.jpeg"
                    alt="Examples of how Okio helps users explore and identify the world around them"
                    className="h-auto w-full object-cover"
                  />
                </div>
              </section>
            )}

            {stepIndex === 1 && (
              <section className="space-y-6">
                <div className="max-w-4xl space-y-3">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                    What are you into right now?
                  </h2>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    Pick a few interests. We will use them to shape your starter guides and the first things you see in the app.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {tagsData.tags.map((tag) => {
                    const isSelected = selectedTags.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`rounded-3xl border p-4 text-left transition ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50 shadow-[0_8px_30px_rgba(16,185,129,0.12)] dark:border-emerald-400 dark:bg-emerald-500/10'
                            : 'border-black/10 bg-[#faf8f2] hover:bg-[#f5f1e7] dark:border-white/10 dark:bg-[#111714] dark:hover:bg-[#18201b]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2">
                            <div className="text-2xl">{tag.icon}</div>
                            <div>
                              <p className="text-base font-semibold text-gray-900 dark:text-white">{tag.label}</p>
                              <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{tag.description}</p>
                            </div>
                          </div>
                          <div
                            className={`mt-1 flex h-6 w-6 min-h-6 min-w-6 shrink-0 items-center justify-center self-start rounded-full border ${
                              isSelected
                                ? 'border-emerald-500 bg-emerald-500 text-white'
                                : 'border-gray-300 bg-white dark:border-white/20 dark:bg-[#0f1311]'
                            }`}
                          >
                            {isSelected && <CheckIcon className="h-4 w-4" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select as many as you like. More signal means better starter recommendations.
                </p>
              </section>
            )}

            {stepIndex === 2 && (
              <section className="space-y-6">
                <div className="max-w-4xl space-y-3">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                    {selectedTags.length > 0 ? 'Starter guides picked for you' : 'Popular starter guides'}
                  </h2>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    {selectedTags.length > 0
                      ? `Based on ${selectedTagLabels.join(', ')}, here are some starter projects that should feel like a strong first fit.`
                      : 'You can still get started right away. Here are a few popular starter projects to begin exploring.'}
                  </p>
                </div>

                {isPreviewLoading ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={index} className="h-56 animate-pulse rounded-3xl bg-[#f3efe5] dark:bg-[#111714]" />
                    ))}
                  </div>
                ) : previewProjects.length === 0 ? (
                  <div className="rounded-[2rem] border border-dashed border-black/15 bg-[#faf8f2] px-5 py-10 text-center dark:border-white/10 dark:bg-[#111714]">
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      We are still warming up your starter feed
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                      Try continuing and browse the full starter list, or go back and pick a few more interests for stronger recommendations.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {previewProjects.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => handleSelectStarter(project.id)}
                        className={`overflow-hidden rounded-3xl border text-left transition active:scale-[0.99] ${
                          selectedStarterId === project.id
                            ? 'border-emerald-500 bg-emerald-50 shadow-[0_10px_30px_rgba(16,185,129,0.16)] dark:border-emerald-500 dark:bg-emerald-500/10'
                            : 'border-black/10 bg-[#faf8f2] hover:border-emerald-400 hover:shadow-[0_10px_30px_rgba(16,185,129,0.12)] dark:border-white/10 dark:bg-[#111714] dark:hover:border-emerald-500/60'
                        }`}
                      >
                        <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800">
                          {project.images?.[0] ? (
                            <img
                              src={getImageUrl(project.images[0], 'cardtablet')}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-4xl opacity-40">
                              {tagsData.tags.find((tag) => project.tags?.includes(tag.id))?.icon || '🌿'}
                            </div>
                          )}
                        </div>
                        <div className="space-y-3 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-lg font-semibold text-gray-900 dark:text-white">{project.name}</p>
                              <p className="mt-1 line-clamp-3 text-sm text-gray-600 dark:text-gray-400">
                                {project.description}
                              </p>
                            </div>
                            <div
                              className={`mt-1 flex h-6 w-6 min-h-6 min-w-6 shrink-0 items-center justify-center self-start rounded-full border ${
                                selectedStarterId === project.id
                                  ? 'border-emerald-500 bg-emerald-500 text-white'
                                  : 'border-gray-300 bg-white dark:border-white/20 dark:bg-[#0f1311]'
                              }`}
                            >
                              {selectedStarterId === project.id && <CheckIcon className="h-4 w-4" />}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(project.tags || []).slice(0, 3).map((tagId) => {
                              const tag = tagsData.tags.find((item) => item.id === tagId);
                              if (!tag) return null;
                              return (
                                <span
                                  key={tagId}
                                  className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-[#0f1311] dark:text-gray-300"
                                >
                                  <span>{tag.icon}</span>
                                  <span>{tag.label}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {stepIndex === 3 && (
              <section className="space-y-6">
                <div className="max-w-4xl space-y-3">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                    {selectedStarter ? `Start with ${selectedStarter.name}` : 'Ready to go deeper with Okio?'}
                  </h2>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    {selectedStarter
                      ? 'You picked a starter. Upgrade to jump straight into a live project from that guide, or continue without upgrading to explore the starter in more detail first.'
                      : 'Unlock guided identification, richer project support, and deeper AI help before you jump into your personalized starter feed.'}
                  </p>
                </div>
                <div className="rounded-[2rem] border border-emerald-500/20 bg-[#101611] px-5 py-6 text-white shadow-[0_18px_50px_rgba(0,0,0,0.18)] sm:px-6">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/15">
                    <span className="text-2xl">🌿</span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xl font-bold">
                      Get 100 tokens and start fully unlocked
                    </p>
                    <p className="text-sm leading-relaxed text-white/75">
                      Best for identification, guided starter projects, and deeper AI help right from your first session.
                    </p>
                  </div>
                  <div className="mt-5 space-y-3 rounded-3xl bg-white/5 p-4">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 text-emerald-300">•</span>
                      <div>
                        <p className="text-sm font-semibold text-white">Identify what you find</p>
                        <p className="text-sm text-white/70">Plants, fungi, wildlife, rocks, and more.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 text-emerald-300">•</span>
                      <div>
                        <p className="text-sm font-semibold text-white">Follow guided project steps</p>
                        <p className="text-sm text-white/70">
                          {selectedStarter
                            ? `We will launch a fresh project from ${selectedStarter.name} after checkout.`
                            : 'Use your personalized starter guides as a launch point for real-world exploration.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-white/45">Starter pack</p>
                      <p className="mt-1 text-3xl font-bold">$5</p>
                    </div>
                    <p className="text-sm text-white/60">One-time purchase</p>
                  </div>
                </div>
                <form
                  onSubmit={handleRedeemVoucher}
                  className="rounded-[1.5rem] border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5"
                >
                  <label
                    htmlFor="onboarding-voucher-code"
                    className="block text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    Or redeem a voucher for a token pack
                  </label>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Have a code from the Okio team? Redeem it here to unlock your starter tokens.
                  </p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <input
                      id="onboarding-voucher-code"
                      type="text"
                      value={voucherCode}
                      onChange={(event) => setVoucherCode(event.target.value)}
                      placeholder="Enter voucher code"
                      autoCapitalize="characters"
                      autoComplete="off"
                      disabled={isRedeemingVoucher || isSaving}
                      className="min-h-[48px] flex-1 rounded-2xl border border-gray-300 bg-white px-4 text-sm font-medium tracking-wide text-gray-900 shadow-sm outline-none transition placeholder:normal-case placeholder:tracking-normal placeholder:text-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-white/10 dark:bg-[#0f1311] dark:text-white dark:placeholder:text-gray-500"
                    />
                    <button
                      type="submit"
                      disabled={isRedeemingVoucher || isSaving || !voucherCode.trim()}
                      className="min-h-[48px] rounded-2xl bg-[#163b22] px-5 text-sm font-semibold text-white transition hover:bg-[#1b4a2a] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                    >
                      {isRedeemingVoucher ? 'Redeeming...' : 'Redeem code'}
                    </button>
                  </div>
                </form>
              </section>
            )}
          </div>

          <div className="space-y-3 border-t border-black/10 pt-5 dark:border-white/10">
            {stepIndex === 3 ? (
              <>
                <button
                  type="button"
                  onClick={handleInlineUpgrade}
                  disabled={isPurchasing || isSaving || isRedeemingVoucher}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-4 text-base font-semibold text-white transition hover:bg-emerald-500"
                >
                  {isPurchasing ? 'Opening checkout...' : 'Get 100 tokens · $5'}
                </button>
                <button
                  type="button"
                  onClick={finishOnboarding}
                  disabled={isSaving || isRedeemingVoucher}
                  className="mx-auto block text-xs font-medium text-gray-500 underline-offset-4 transition hover:text-gray-700 hover:underline disabled:opacity-60 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {isSaving
                    ? 'Saving your setup...'
                    : selectedStarter
                      ? 'Continue to starter details without upgrading'
                      : 'Continue without upgrading'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleContinue}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#163b22] px-5 py-4 text-base font-semibold text-white transition hover:bg-[#1b4a2a] dark:bg-emerald-600 dark:hover:bg-emerald-500"
              >
                Continue
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            )}

            {stepIndex === 1 && (
              <button
                type="button"
                onClick={() => setStepIndex(2)}
                className="w-full text-sm font-medium text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
